import React from 'react';

// Shared done/total completion bar. Extracted from the GagDetailPanel green
// progress bar so every completion indicator uses one visual + one pct rule.
//
// The component takes ALREADY-computed done/total — callers apply their own
// counting rule (e.g. gag panels exclude cancelled tasks before passing).
// It knows nothing about task status; it just draws done/total as a bar.
//
// Props:
//   done, total   — counts (total===0 is guarded → neutral 0% track, no NaN)
//   showLabel      — when true, prefixes "{done}/{total} {labelSuffix}"
//   labelSuffix    — label tail (default 'tasks done')
//   className      — sizes the bar track (e.g. 'w-16', 'flex-1 max-w-xs')
//   ariaLabel      — OPTIONAL accessible name for the progressbar (2026-09-14,
//                    the upload bar: "Uploading <name>"). Absent → no attribute,
//                    so every existing caller renders byte-identically.
//   pending        — OPTIONAL (2026-10-28, per-line "ordered"): a count of a
//                    SECOND, lighter segment drawn after `done` — the order
//                    table's "ordered, not yet delivered" lines. null (the
//                    default) → the one-fill bar, byte-identical. Colour is
//                    never the sole carrier: the caller prints the counts.
//   valueText      — OPTIONAL aria-valuetext (segmented mode's words).

// Segment widths in percent: the done segment, then the pending one, each
// rounded and clamped so done + pending never exceeds 100.
export function progressSegmentWidths(done, pending, total) {
  if (!(total > 0)) return { done: 0, pending: 0 };
  const d = Math.min(100, Math.max(0, Math.round((done / total) * 100)));
  const p = Math.min(100 - d, Math.max(0, Math.round(((done + pending) / total) * 100) - d));
  return { done: d, pending: p };
}

// Segmented fills, measured 2026-10-28 (track = gray-200 light; dark
// `html.dark .bg-gray-200` #475569 beats `dark:bg-gray-600`): see the
// commit for the figures. A 1px gap-coloured edge separates the segments.
// ONE green per segment in BOTH themes (owner 2026-10-28: 'keep the same green for dark and light mode').
const SEGMENT_DONE = 'h-full bg-green-600';
// Faded 50% (owner 2026-10-28: "fade the ordered bar 50% more") so it reads as not-yet-delivered.
// 🔀 LIGHT mode only: green-400, unfaded (owner 2026-10-28, yes to "a stronger light-mode
// ordered green") — the faded green-300 measured ~1.03:1 against the gray-200 to-order block.
// green-400 sits between them: ~1.4:1 vs the track, ~1.9:1 vs green-600. Dark is unchanged.
const SEGMENT_PENDING = 'h-full bg-green-400 dark:bg-green-300 dark:opacity-50';

export const PER_ITEM_MAX = 12;

function ProgressBar({ done, total, pending = null, perItem = false, valueText = undefined, showLabel = false, labelSuffix = 'tasks done', className = '', ariaLabel = undefined }) {
  // Guard total===0 so an empty rollup renders a neutral 0% track (no NaN,
  // no divide-by-zero). Same rule as the panel's inline bar.
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  // PER-ITEM blocks (owner 2026-10-28: "vertical segments to the bar for each item? like a
  // catapila body"): one block per item, delivered first, then ordered, then to-order — only up
  // to PER_ITEM_MAX items; a longer order keeps the smooth bar (29 blocks would be slivers).
  if (pending != null && perItem && total > 0 && total <= PER_ITEM_MAX) {
    const d = Math.max(0, Math.min(total, done));
    const p = Math.max(0, Math.min(total - d, pending));
    const cells = Array.from({ length: total }, (_, i) => (i < d ? 'done' : i < d + p ? 'pending' : 'todo'));
    return (
      <div
        data-testid="progress-bar"
        data-per-item="true"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={ariaLabel}
        aria-valuetext={valueText}
        // A GRID with a 2px gap, not flex + gap-px: a 1px gap between blocks whose widths are
        // fractional pixels rounds to 0–2px, so the gaps looked uneven (owner 2026-10-28).
        className={`grid h-2 gap-[2px] ${className}`}
        style={{ gridTemplateColumns: `repeat(${total}, minmax(0, 1fr))` }}
      >
        {cells.map((kind, i) => (
          <div
            key={i}
            data-testid={`progress-cell-${kind}`}
            className={`${i === 0 ? 'rounded-l-full' : ''} ${i === total - 1 ? 'rounded-r-full' : ''} ${
              kind === 'done' ? SEGMENT_DONE : kind === 'pending' ? SEGMENT_PENDING : 'h-full bg-gray-200 dark:bg-gray-600'}`}
          />
        ))}
      </div>
    );
  }

  if (pending != null) {
    const w = progressSegmentWidths(done, pending, total);
    return (
      <div
        data-testid="progress-bar"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={ariaLabel}
        aria-valuetext={valueText}
        className={`flex h-2 rounded-full bg-gray-200 dark:bg-gray-600 overflow-hidden ${className}`}
      >
        {w.done > 0 && <div data-testid="progress-bar-done" className={SEGMENT_DONE} style={{ width: `${w.done}%` }} />}
        {w.pending > 0 && (
          <div
            data-testid="progress-bar-pending"
            className={`${SEGMENT_PENDING}${w.done > 0 ? ' border-l border-white dark:border-gray-800' : ''}`}
            style={{ width: `${w.pending}%` }}
          />
        )}
      </div>
    );
  }

  const bar = (
    <div
      data-testid="progress-bar"
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={ariaLabel}
      className={`h-2 rounded-full bg-gray-200 dark:bg-gray-600 overflow-hidden ${className}`}
    >
      <div
        className="h-full rounded-full bg-green-500 dark:bg-green-500/70 transition-all"
        style={{ width: `${pct}%` }}
      />
    </div>
  );

  if (!showLabel) return bar;

  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
        {done}/{total} {labelSuffix}
      </span>
      <div className="flex-1 min-w-[120px] max-w-xs">{bar}</div>
    </div>
  );
}

export default ProgressBar;
